import { NgZone, untracked } from '@angular/core';
import { Store } from '@ngrx/store';
import * as turf from '@turf/turf';
import { MapMouseEvent } from 'mapbox-gl';
import { Subscription } from 'rxjs';

import { FilterMethod } from '@core/models/data-visualization/data-visualization.state';
import {
  GeoArcEditSessionService,
  NEW_ARC_UID,
  shouldApplyIncomingRoute
} from '@core/services/geo-arc-edit-session/geo-arc-edit-session.service';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import { setFilterOnAggregator } from '@core/store/data-visualization/data-visualization.actions';

import { IconMapGalia } from '@features/components/visualization/mapbox/icon-map-galia';
import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';
import { MapSource } from '@features/components/visualization/mapbox/map-elements/map-source';
import { MapEvents } from '@features/components/visualization/mapbox/map-events';
import { GaliaRoute } from '@features/components/visualization/mapbox/models/galia-route';
import { MapRegistryService } from '@features/components/visualization/mapbox/services/registry.service';

interface RoutePath {
  full?: GeoJSON.Feature;
  initial: GeoJSON.Position[];
}

export class MapRoutes implements MapElement {
  private readonly layerDefault: string;
  private readonly layerCasing: string;
  private readonly layerInvisible: string;
  private readonly layerSymbols: string;
  private readonly layerDashed: string;
  private readonly source: MapSource;
  private dataPaths: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection' as const,
    features: []
  };
  private hoveredPathStateId: string | undefined = undefined;
  /** Everything the store last sent, unfiltered, so a session starting or ending can re-render. */
  private lastReceived: GaliaRoute[] = [];
  private routeMap: Map<string, RoutePath> = new Map();
  private simplificationLevel: number = 0;
  private subscription: Subscription | undefined;
  private eventCleanupFunctions: Array<() => void> = [];

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly store: Store,
    private readonly registry: MapRegistryService,
    private readonly session: GeoArcEditSessionService,
    private readonly zone: NgZone,
    private readonly focus: VisualFocusService
  ) {
    const layer = this.registry.getRouteLayers();
    this.layerDefault = layer.default;
    this.layerCasing = layer.casing;
    this.layerInvisible = layer.invisible;
    this.layerSymbols = layer.symbols;
    this.layerDashed = layer.dashed;

    const source = this.registry.getRouteSources();
    this.source = new MapSource(source.default, this.map);
  }

  destroy(): void {
    this.subscription?.unsubscribe();

    this.eventCleanupFunctions.forEach((cleanup) => cleanup());
    this.eventCleanupFunctions = [];
    // No `mouseleave` reaches a handler that has just been unbound, so a pointer resting on a
    // route line as the map goes away would leave the hover raised for good.
    this.focus.clearHover('route');

    this.source.cleanup();

    this.dataPaths = { type: 'FeatureCollection', features: [] };
    this.lastReceived = [];
    this.routeMap.clear();
    this.hoveredPathStateId = undefined;
  }

  init(): void {
    this.initSource();
    this.initLayer();
    this.subscription = MapEvents.getInstance().on<GaliaRoute[]>(
      'update-routes',
      this.updatePaths.bind(this)
    );
  }

  registerEvents(): void {
    const mouseEnterFn = (e: MapMouseEvent) => {
      if (e.features) this.changeToHoverState(e.features, this.source.name);
    };
    this.map.on('mouseenter', this.layerInvisible, mouseEnterFn);
    this.eventCleanupFunctions.push(() => {
      this.map.off('mouseenter', this.layerInvisible, mouseEnterFn);
    });

    const mouseLeaveFn = () =>
      this.changeToNotHoverState(this.hoveredPathStateId!, this.source.name);
    this.map.on('mouseleave', this.layerInvisible, mouseLeaveFn);
    this.eventCleanupFunctions.push(() => {
      this.map.off('mouseleave', this.layerInvisible, mouseLeaveFn);
    });

    const clickFn = (e: MapMouseEvent) => {
      // A stop sits on its own route's line and its hit circle is wider than the line is
      // thin, so one physical click reaches both hit layers. The stop is the more specific
      // target and wins: hard-filtering to this route would hide the other routes carrying
      // the legs of the job the same click pins. Queried rather than read off a flag the
      // marker handler sets, because the two handlers' firing order is not guaranteed.
      if (this.hitsMarker(e.point)) return;

      const clustersAtPoint = this.map.queryRenderedFeatures(e.point, {
        layers: [this.registry.getMarkerLayers().clustered]
      });

      if (clustersAtPoint.length > 0) return;

      if (e.features && e.features.length > 0) {
        const structuralAggregatorId: string[] = [
          e.features[0].properties!['structuralAggregatorIds']
        ];

        this.store.dispatch(
          setFilterOnAggregator({
            filteredAggregatorIds: structuralAggregatorId,
            filterMethod: FilterMethod.BY_AGGREGATOR
          })
        );
      }
    };

    this.map.on('click', this.layerInvisible, clickFn);
    this.eventCleanupFunctions.push(() => {
      this.map.off('click', this.layerInvisible, clickFn);
    });

    this.map.on('zoomend', () => this.simplifyData());
    this.map.on('moveend', () => this.simplifyData('moveend'));
    this.eventCleanupFunctions.push(() => {
      this.map.off('zoomend', () => this.simplifyData());
      this.map.off('moveend', () => this.simplifyData('moveend'));
    });

    this.zone.runOutsideAngular(() => {
      // A stop marker sits on its own route's line, so the two hit layers overlap and both
      // fire on every frame the pointer holds over a stop. `coversRoute` recognises a
      // hovered action of this route as already covering it — the marker is the more
      // specific target — so this bails instead of re-affirming the route once per frame.
      const onFocusMove = (e: MapMouseEvent) => {
        const uid =
          (e.features?.at(0)?.properties?.['structuralAggregatorIds'] as
            | string
            | undefined) ?? null;
        if (uid !== null && this.focus.coversRoute(uid)) return;
        this.zone.run(() => this.focus.hoverRoute(uid));
      };
      this.map.on('mousemove', this.layerInvisible, onFocusMove);
      this.eventCleanupFunctions.push(() => {
        this.map.off('mousemove', this.layerInvisible, onFocusMove);
      });

      const onFocusLeave = () => {
        if (this.focus.currentHoverTarget()?.kind !== 'route') return;
        this.zone.run(() => this.focus.hoverRoute(null));
      };
      this.map.on('mouseleave', this.layerInvisible, onFocusLeave);
      this.eventCleanupFunctions.push(() => {
        this.map.off('mouseleave', this.layerInvisible, onFocusLeave);
      });
    });
  }

  /**
   * The layers `MapMarkers` listens on for its own click. `queryRenderedFeatures` throws on a
   * layer the style does not hold, so a map carrying no markers is filtered out first.
   */
  private hitsMarker(point: mapboxgl.Point): boolean {
    const markers = this.registry.getMarkerLayers();
    const layers = [
      markers.invisible,
      markers.unclusteredInvisible,
      markers.unclusteredSpiderfy
    ].filter((layer) => this.map.getLayer(layer));

    return (
      layers.length > 0 && this.map.queryRenderedFeatures(point, { layers }).length > 0
    );
  }

  private simplifyData(fromEvent: 'moveend' | 'zoom' = 'zoom') {
    const tolerance =
      fromEvent === 'zoom'
        ? this.getToleranceLevel()
        : this.getToleranceLevel(undefined, true);

    if (tolerance > 0) {
      const visibleFeatures = this.map.queryRenderedFeatures({
        target: { layerId: this.layerDefault }
      });

      if (visibleFeatures.length > 0) {
        visibleFeatures.forEach((vf) => {
          const id = vf.properties!['name'];
          const feature = this.dataPaths.features.find((f) => f.id === id);
          if (feature && feature.properties?.['tolerance'] !== tolerance) {
            const routePath = this.getRoutePath(feature.id as string);
            if ('coordinates' in feature.geometry) {
              const simplified = turf.simplify(routePath.full!, {
                tolerance
              }).geometry as GeoJSON.LineString;

              feature.geometry.coordinates = simplified.coordinates;
            }
            feature.properties!['tolerance'] = tolerance;
          }
        });
        (this.map.getSource(this.source.name) as mapboxgl.GeoJSONSource).setData(
          this.dataPaths
        );
      }
    }
  }

  private changeToHoverState(features: mapboxgl.GeoJSONFeature[], featureSource: string) {
    this.map.getCanvas().style.cursor = 'pointer';
    if (features.length > 0 && features[0].id) {
      this.map.setFeatureState(
        { source: featureSource, id: features[0].id },
        { hover: true }
      );
      this.hoveredPathStateId = features[0].id as string;
    }
  }

  private changeToNotHoverState(id: string, featureSource: string) {
    if (id != null) {
      this.map.getCanvas().style.cursor = '';
      this.map.setFeatureState({ source: featureSource, id: id }, { hover: false });
      this.hoveredPathStateId = undefined;
    }
  }

  /**
   * Creates a GeoJSON FeatureCollection from the provided routes.
   *
   * @private
   * @param {GaliaRoute[]} routes - The routes to convert into GeoJSON features.
   * @returns {GeoJSON.FeatureCollection} - A GeoJSON FeatureCollection containing the
   *   routes.
   */
  private createMapboxRoutes(routes: GaliaRoute[]): GeoJSON.FeatureCollection {
    const geoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: []
    };

    if (routes.length === 0) {
      return geoJson;
    }

    for (const path of routes) {
      const type = 'Feature' as const;
      const id = path.uid;
      const coordinates = path.coords.map((coord) => [
        coord.lng,
        coord.lat
      ]) as GeoJSON.Position[];
      // turf.lineString throws below on fewer than two positions, which would abort the
      // whole render and leave the source holding the previous frame.
      if (coordinates.length < 2) {
        continue;
      }
      const linePath = turf.lineString(coordinates);

      const tolerance = this.getToleranceLevel(0.1, true);
      const { coordinates: simplified } = turf.simplify(linePath, {
        tolerance
      }).geometry;
      const geometry = {
        type: 'LineString' as const,
        coordinates: simplified
      };

      const color = path.color ?? '#4e4c4c';
      const arrowType = 'arrow-road';
      const arrowIconName = `${arrowType}-${color.replace('#', '')}`;

      // Ensure arrow icon exists (lazy generation)
      IconMapGalia.ensureIconExists(this.map, arrowType, color);

      const properties = {
        id: id,
        name: path.name,
        structuralAggregatorIds: path.uid,
        profile: path.profileType,
        restrictedAreas: path.areaData,
        transportType: path.transportType,
        tolerance: 0.1,
        color: color,
        width: path.width,
        arrow: arrowIconName,
        dashed: path.dashed === true
      };

      geoJson.features.push({
        type,
        id,
        geometry,
        properties
      } as GeoJSON.Feature);

      this.setBaseRoutePath(id, linePath, coordinates);
    }

    return geoJson;
  }

  private setBaseRoutePath(
    featureId: string,
    linePath: GeoJSON.Feature,
    coords: GeoJSON.Position[]
  ) {
    const routePath = this.getRoutePath(featureId);
    routePath.full = linePath;
    routePath.initial = coords;
  }

  private getRoutePath(featureId: string): RoutePath {
    if (!this.routeMap.has(featureId)) {
      this.routeMap.set(featureId, { full: undefined, initial: [] });
    }
    return this.routeMap.get(featureId) as RoutePath;
  }

  /**
   * Get the tolerance level based on the current zoom level of the map.
   *
   * @private
   * @param {number} defaultTolerance - Default tolerance level to return if no zoom level
   *   matches.
   * @param {boolean} forceGet - If true, forces the retrieval of the tolerance level even
   *   if it hasn't changed.
   * @returns {number} The tolerance level based on the zoom level.
   */
  private getToleranceLevel(defaultTolerance?: number, forceGet?: boolean): number {
    const zoomLevel = this.map.getZoom();
    if (zoomLevel >= 14 && (this.simplificationLevel != 14 || forceGet)) {
      this.simplificationLevel = 14;
      return 0.000001;
    } else if (
      zoomLevel >= 10 &&
      zoomLevel < 14 &&
      (this.simplificationLevel != 10 || forceGet)
    ) {
      this.simplificationLevel = 10;
      return 0.0001;
    } else if (
      zoomLevel >= 6 &&
      zoomLevel < 10 &&
      (this.simplificationLevel != 6 || forceGet)
    ) {
      this.simplificationLevel = 6;
      return 0.001;
    } else if (
      zoomLevel <= 5 &&
      (this.simplificationLevel != 5 || forceGet) &&
      this.simplificationLevel != 0
    ) {
      this.simplificationLevel = 5;
      return 0.1;
    }
    return defaultTolerance ? defaultTolerance : 0;
  }

  /**
   * Initializes the layer for the routes on the map.
   *
   * @private
   */
  private initLayer() {
    // Added first so it sits below every other route layer: `addLayer` with no `beforeId`
    // appends, and the last layer in the order draws on top. It carries no focus logic of its
    // own — `MapFocusService` raises its opacity for the lit route and leaves it at the
    // declared 0 everywhere else, so a white halo separates the focused line from the network
    // and from the basemap's own roads.
    this.map.addLayer({
      id: this.layerCasing,
      type: 'line',
      source: this.source.name,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#ffffff',
        // Sized against the width the lit line draws at, which is the declared one. The
        // visible halo is the 6px difference between the two, a flat 3px per side at any
        // route width.
        'line-width': ['+', ['get', 'width'], 6],
        'line-opacity': 0
      }
    });

    // The dashed/solid split rides on `line-opacity`, not on a layer `filter`:
    // MapFilterService replaces the filter of every route layer whenever it filters or
    // resets, which would leave a dashed feature painted solid here as well.
    this.map.addLayer({
      id: this.layerDefault,
      type: 'line',
      source: this.source.name,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['case', ['==', ['get', 'dashed'], true], 0, 0.5],
        'line-blur': 0.5
      }
    });

    // An arc with no stored geometry. Dashed so it never reads as a road.
    this.map.addLayer({
      id: this.layerDashed,
      type: 'line',
      source: this.source.name,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['case', ['==', ['get', 'dashed'], true], 0.7, 0],
        'line-dasharray': [2, 2]
      }
    });

    // Path symbols
    this.map.addLayer({
      id: this.layerSymbols,
      type: 'symbol',
      source: this.source.name,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 100,
        'symbol-avoid-edges': false,
        'icon-image': ['get', 'arrow'],
        'icon-size': 0.4,
        'icon-allow-overlap': true
      },
      paint: {
        'icon-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0]
      }
    });

    this.map.addLayer({
      id: this.layerInvisible,
      type: 'line',
      source: this.source.name,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 30,
        'line-opacity': 0
      }
    });
  }

  /**
   * Initializes the source for the routes on the map.
   *
   * @private
   */
  private initSource() {
    this.map.addSource(this.source.name, {
      type: 'geojson',
      data: this.dataPaths,
      dynamic: true,
      promoteId: 'id'
    });
  }

  /**
   * Re-renders against the current session. Called from a component effect rather than by
   * re-emitting on the event bus, whose distinctUntilChanged would drop an identical array.
   * Without it the arc under edit keeps its stored line, its arrows and its 30 px hit strip
   * for the whole session, competing with the editor's own line and handlers.
   */
  syncSession(): void {
    this.render();
  }

  /**
   * Updates the paths on the map with the provided routes.
   *
   * @private
   * @param {GaliaRoute[]} routes - The routes to update on the map.
   */
  private updatePaths(routes: GaliaRoute[]) {
    this.lastReceived = routes;
    this.render();
  }

  private render() {
    const routes = this.lastReceived;
    // While a drag is in flight the editor owns this arc's geometry; letting the
    // store's copy through would clobber the gesture mid-flight.
    // Untracked: updatePaths runs synchronously inside MapboxComponent's routes effect, so
    // an ordinary read would make that effect — and the camera framing it calls — re-run on
    // every session boundary.
    const activeUid = untracked(() => this.session.activeUid());
    // An arc being created has no stable uid to match on: the form still emits whatever
    // uid the user has typed, which shouldApplyIncomingRoute cannot recognise as "under
    // edit". Withholding every route for the duration of a new-arc session is the only way
    // to avoid drawing a second, lagging copy beneath the editor's live line.
    const visible =
      activeUid === NEW_ARC_UID
        ? []
        : routes.filter((route) => shouldApplyIncomingRoute(route.uid, activeUid));
    this.dataPaths = this.createMapboxRoutes(visible);
    this.source.updateSourceOnReady(this.dataPaths);
  }
}
